/*
 * The reader's place in the thread holds while the thread changes above it (LEA-514). The thread
 * reads newest first, so a new message, the reply going out, a box opening and an email measuring
 * its height all land above whatever a reader scrolled down to older mail is looking at. Native
 * scroll anchoring would do this, but stable Safari has no `overflow-anchor` (caniuse through 26.4;
 * WebKit bug 171099 open; checked 2026-11-04), so this does it in every browser, and the stylesheet
 * turns the native one off on the scroller so Chromium and Firefox never correct twice. A reader at
 * the top is left alone: that is where the newest arrives, and where the reply box is.
 */
import { type RefObject, useEffect, useLayoutEffect } from "react";

/**
 * Sent from an item of the pane (a message's `li`) just before a box opens above it: that item keeps
 * its place while the box takes room, whatever the reader had at the top (`thread-boxes.tsx`).
 */
export const HOLD_VIEW_EVENT = "leadcue-hold-view";

/** What can hold the view: the pane's own items (the box, the agent's item) and each message. */
const ANCHORS = ":scope > :not(.lcr-i2-messages), :scope > .lcr-i2-messages > li";

/**
 * The element that scrolls: the pane on a desktop; on a phone the whole conversation scrolls, head
 * included, and the pane only grows (`styles.css`, the phone's `.lcr-i2-reading`).
 */
function scrollerOf(pane: HTMLElement): HTMLElement {
  if (getComputedStyle(pane).overflowY !== "visible") return pane;
  return pane.closest<HTMLElement>(".lcr-i2-reading") ?? pane;
}

type Anchor = { node: Element; offset: number };

/** The first item with height whose bottom is below the scroller's top, and where it sits. */
function anchorIn(pane: HTMLElement, scroller: HTMLElement): Anchor | null {
  if (scroller.scrollTop <= 0) return null;
  const top = scroller.getBoundingClientRect().top;
  for (const node of pane.querySelectorAll(ANCHORS)) {
    const box = node.getBoundingClientRect();
    if (box.height > 0 && box.bottom > top) return { node, offset: box.top - top };
  }
  return null;
}

/**
 * Holds the first item in view where it was while the pane's content changes size or gains and loses
 * items. Read on every scroll, restored on every change, so a correction is never stale. A scroll the
 * app makes itself (a link to a message, then its focus) is read at the focus as well: its scroll
 * event comes a frame later, after an email above may already have grown (measured in Chromium:
 * the card moved 640 px down, uncorrected). Conversation `id` opens at the top, its reply box first,
 * whatever the pane showed before.
 */
export function useViewHeld(pane: RefObject<HTMLElement | null>, id: string): void {
  useLayoutEffect(() => {
    const element = pane.current;
    if (element && id) scrollerOf(element).scrollTop = 0;
  }, [pane, id]);
  useEffect(() => {
    const element = pane.current;
    if (
      !element ||
      typeof ResizeObserver === "undefined" ||
      typeof MutationObserver === "undefined"
    )
      return;
    let anchor: Anchor | null = null;
    const record = () => {
      anchor = anchorIn(element, scrollerOf(element));
    };
    const hold = () => {
      const scroller = scrollerOf(element);
      if (anchor?.node.isConnected) {
        const top = scroller.getBoundingClientRect().top;
        const drift = anchor.node.getBoundingClientRect().top - top - anchor.offset;
        if (Math.abs(drift) >= 1) scroller.scrollTop += drift;
      }
      record();
    };
    const sizes = new ResizeObserver(hold);
    const watch = () => {
      sizes.disconnect();
      for (const child of element.children) sizes.observe(child);
    };
    const items = new MutationObserver(() => {
      watch();
      hold();
    });
    watch();
    record();
    items.observe(element, { childList: true });
    element.addEventListener("focusin", record);
    const holdItem = (event: Event) => {
      const node = event.target;
      if (!(node instanceof Element)) return;
      const top = scrollerOf(element).getBoundingClientRect().top;
      anchor = { node, offset: node.getBoundingClientRect().top - top };
    };
    element.addEventListener(HOLD_VIEW_EVENT, holdItem);
    const scrollers = new Set([element, scrollerOf(element)]);
    for (const scroller of scrollers)
      scroller.addEventListener("scroll", record, { passive: true });
    return () => {
      sizes.disconnect();
      items.disconnect();
      element.removeEventListener("focusin", record);
      element.removeEventListener(HOLD_VIEW_EVENT, holdItem);
      for (const scroller of scrollers) scroller.removeEventListener("scroll", record);
    };
  }, [pane]);
}
